import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { Button, Link } from '@eye-able/ui';
import { Mail } from 'lucide-react';
import { useSecondsUntil } from './useSecondsUntil';

interface SendCodeButtonProps {
  /** A code went out; the button then offers the next one after the cooldown. */
  sent: boolean;
  /** When the API accepts the next request; null when a code may be sent now. */
  resendAt: Date | null;
  onSend: () => void;
  disabled?: boolean;
  /** Names the address in a status line once a code went out. */
  email?: string;
  /** A full-width button instead of a link, for the step where sending is the only action. */
  prominent?: boolean;
}

/**
 * Sends a mailed code, then counts down to the next one. One element for all
 * states, so keyboard focus stays put and the status line says what happened.
 */
function SendCodeButton({
  sent,
  resendAt,
  onSend,
  disabled = false,
  email,
  prominent = false,
}: SendCodeButtonProps) {
  const { t } = useInlineTranslation();
  const seconds = useSecondsUntil(resendAt);
  const waiting = sent && seconds > 0;
  const label = !sent
    ? t('mfaSendEmailCode')
    : waiting
      ? t('mfaResendCodeIn', { seconds })
      : t('mfaResendCode');

  return (
    <div className='eye:flex eye:flex-col eye:items-start eye:gap-8'>
      {email ? (
        <p role='status' className='eye:text-14 eye:leading-22'>
          {sent ? <T k='mfaEmailCodeSentTo' options={{ email }} /> : null}
        </p>
      ) : null}
      {prominent && !sent ? (
        <Button
          type='button'
          variant='outline'
          className='eye:w-full eye:self-stretch'
          onClick={onSend}
          disabled={disabled}
        >
          <Mail aria-hidden />
          {label}
        </Button>
      ) : (
        <Link size='small' asChild>
          <button
            type='button'
            className='eye:cursor-pointer eye:disabled:cursor-default eye:disabled:no-underline eye:disabled:text-grey-120'
            onClick={onSend}
            disabled={disabled || waiting}
          >
            {label}
          </button>
        </Link>
      )}
    </div>
  );
}

export default SendCodeButton;
